import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { puoFare } from '../utils/permessi';
import { FEDERAZIONI, etichetta, scadenzaInfo } from '../utils/tessere';

const stagioneCorrente = () => {
  const oggi = new Date();
  const annoInizio = oggi.getMonth() >= 8 ? oggi.getFullYear() : oggi.getFullYear() - 1; // stagione sportiva 1/9–31/8 (decisione titolare)
  return `${annoInizio}/${annoInizio + 1}`;
};

const emptyForm = { federazione: 'ASI_B', numero_tessera: '', stagione: stagioneCorrente(), data_scadenza: '' };

// Pannello riusabile per gestire i tesseramenti (ASI A/B, FIPAV) di una Persona, usato sia
// dalla scheda Cliente sia dalla scheda Dipendente/Allenatore — la tabella è agganciata
// a Persona, non al ruolo, quindi lo stesso componente vale per chiunque.
export default function GestisciTessereStaff({ personaId }) {
  const { user } = useAuth();
  const puoGestire = puoFare(user, 'tesseramenti', 'edit');
  const [tessere, setTessere] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    if (!personaId) { setLoading(false); return; }
    api.get(`/tesseramenti/persona/${personaId}`).then(r => setTessere(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personaId]);

  if (!personaId) {
    return <div style={s.nota}>Nessuna anagrafica unica collegata a questo record — il tesseramento richiede un'anagrafica Persona (creata automaticamente per i nuovi clienti).</div>;
  }
  if (loading) return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    const invia = (forza) => api.post('/tesseramenti', { persona_id: personaId, ...form, data_scadenza: form.data_scadenza || null, forza });
    try {
      try { await invia(false); }
      catch (err) {
        // ASI A a chi ha già la B: si blocca, si può forzare solo confermando (decisione titolare 09/10)
        if (!err.response?.data?.conferma) throw err;
        if (!window.confirm(`${err.response.data.error}.\n\nAggiungere comunque la ASI A?`)) return;
        await invia(true);
      }
      setForm({ ...emptyForm, stagione: form.stagione });
      setShowForm(false);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo tesseramento?')) return;
    await api.delete(`/tesseramenti/${id}`);
    load();
  };

  return (
    <div>
      {tessere.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'10px' }}>
          {tessere.map(t => (
            <div key={t.id} style={s.riga}>
              <div>
                <span style={s.fed}>{etichetta(t.federazione)}</span>
                <span style={{ fontFamily:'monospace', marginLeft:'8px' }}>{t.numero_tessera}</span>
                <span style={{ color:'var(--text-dim)', marginLeft:'8px', fontSize:'var(--font-size-xs)' }}>{t.stagione}</span>
                {scadenzaInfo(t) && <span title={scadenzaInfo(t).titolo} style={{ color: scadenzaInfo(t).colore, marginLeft:'8px', fontSize:'var(--font-size-xs)', fontWeight:600 }}>{scadenzaInfo(t).testo}</span>}
              </div>
              {puoGestire && <button type="button" style={s.btnDel} onClick={() => handleDelete(t.id)}><Trash2 size={13}/></button>}
            </div>
          ))}
        </div>
      )}

      {!showForm && puoGestire && (
        <button type="button" style={s.btnAdd} onClick={() => setShowForm(true)}><Plus size={13}/> AGGIUNGI TESSERAMENTO</button>
      )}

      {showForm && (
        <div style={s.formBox}>
          <div style={{ display:'flex', gap:'8px' }}>
            <select style={{ ...s.input, flex:'0 0 100px' }} value={form.federazione} onChange={e => setForm(f => ({ ...f, federazione: e.target.value }))}>
              {FEDERAZIONI.map(f => <option key={f} value={f}>{etichetta(f)}</option>)}
            </select>
            <input style={{ ...s.input, flex:1 }} placeholder="Numero tessera" value={form.numero_tessera} onChange={e => setForm(f => ({ ...f, numero_tessera: e.target.value }))} />
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <label style={s.campo}>STAGIONE
              <input style={s.input} placeholder="es. 2026/2027" value={form.stagione} onChange={e => setForm(f => ({ ...f, stagione: e.target.value }))} />
            </label>
            <label style={s.campo}>SCADENZA (FACOLTATIVA)
              <input style={s.input} type="date" value={form.data_scadenza} onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))} />
            </label>
          </div>
          <div style={{ fontSize:'var(--font-size-xs)', color:'var(--text-dim)' }}>Lascia vuota la scadenza se la tessera vale tutta la stagione (1 settembre – 31 agosto). Mettila solo se scade prima.</div>
          {error && <div style={s.error}>{error}</div>}
          <div style={{ display:'flex', gap:'8px' }}>
            <button type="button" style={s.btnAnnulla} onClick={() => { setShowForm(false); setError(''); }}>ANNULLA</button>
            <button type="button" style={s.btnSalva} onClick={handleAdd}>SALVA</button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  campo: { flex:1, display:'flex', flexDirection:'column', gap:'4px', fontSize:'var(--font-size-xs)', fontWeight:700, letterSpacing:'.5px', color:'var(--text-muted)' },
  nota:      { color:'var(--text-dim)', fontSize:'var(--font-size-xs)', background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'10px 12px' },
  riga:      { display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'8px 10px', fontSize:'var(--font-size-sm)' },
  fed:       { fontWeight:'700', fontSize:'var(--font-size-xs)', letterSpacing:'1px', color:'#F4600C' },
  btnDel:    { background:'transparent', border:'none', color:'#EF4444', cursor:'pointer', padding:'4px' },
  btnAdd:    { display:'flex', alignItems:'center', gap:'6px', background:'transparent', border:'1px dashed var(--border2)', borderRadius:'8px', padding:'8px 12px', color:'var(--text-dim)', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', width:'100%', justifyContent:'center' },
  formBox:   { display:'flex', flexDirection:'column', gap:'8px', background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'10px' },
  input:     { background:'var(--bg)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'7px 10px', color:'var(--text)', fontSize:'var(--font-size-xs)', outline:'none' },
  error:     { color:'#EF4444', fontSize:'var(--font-size-xs)' },
  btnAnnulla:{ flex:1, background:'transparent', border:'1px solid var(--border2)', borderRadius:'6px', padding:'7px', color:'var(--text-dim)', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSalva:  { flex:1, background:'#F4600C', border:'none', borderRadius:'6px', padding:'7px', color:'#fff', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
};
